import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { SadukaFile } from 'cityads-v3/types/file'
import { makeRequest } from 'cityads-v3/utils/apiUtils'
import { requestWrapper } from 'cityads-v3/utils/apiUtils_ts'

export enum LegalSubjectId {
  legalEntity    = 1,
  soleProprietor = 2,
  selfEmployed   = 3,
}

export type AdvertisingLawForm = {
  country_id      : number,
  last_name       : string,
  first_name      : string,
  middle_name     : string,
  legal_subject   : LegalSubjectId,
  legal_name      : string,
  vat_number      : string,
  psrn            : string,
  legal_address   : string,
  email           : string,
  website_url     : string,
  ownership_rights: SadukaFile[],
}

type AdvertisingLawFormResponse = {
  advertisingLawForm: AdvertisingLawForm | null,
}

type AdvertisingLawFormListResponse = {
  advertisingLawForm: AdvertisingLawForm[],
}

type AdvertisingLawFormCountResponse = {
  meta: {
    total: number
  }
}

export const initialState = {
  data: {} as Partial<AdvertisingLawForm>,
  count: null as (number | null),
  isLoadingForm: false,
  loadingFormError: false,
  isLoadingCount: false,
  loadingCountError: false,
  isSaving: false,
}

function normalizeAdvertisingLawFormCountResponse({ meta: { total } }: AdvertisingLawFormCountResponse): number {
  return total
}

function normalizeAdvertisingLawFormListResponse({ advertisingLawForm }: AdvertisingLawFormListResponse): AdvertisingLawForm | null {
  return advertisingLawForm[0] ?? null
}

function normalizeAdvertisingLawFormResponse({ advertisingLawForm }: AdvertisingLawFormResponse): AdvertisingLawForm {
  return advertisingLawForm
}

export const slice = createSlice({
  name: 'advertisingLawForm',
  initialState,
  reducers: {
    loadCount: state => {
      state.isLoadingCount = true
    },
    loadCountSuccess: (state, action: PayloadAction<AdvertisingLawFormCountResponse>) => {
      state.count = normalizeAdvertisingLawFormCountResponse(action.payload)
      state.isLoadingCount = false
      state.loadingCountError = false
    },
    loadCountFail: state => {
      state.isLoadingCount = false
      state.loadingCountError = true
    },
    loadForm: state => {
      state.isLoadingForm = true
    },
    loadFormSuccess: (state, action: PayloadAction<AdvertisingLawFormListResponse>) => {
      state.data = normalizeAdvertisingLawFormListResponse(action.payload)
      state.isLoadingForm = false
      state.loadingFormError = false
    },
    loadFormFail: state => {
      state.isLoadingForm = false
      state.loadingFormError = true
    },
    saveForm: state => {
      state.isSaving = true
    },
    saveFormSuccess: (state, action: PayloadAction<AdvertisingLawFormResponse>) => {
      state.data = normalizeAdvertisingLawFormResponse(action.payload)
      state.count = state.count || 1
      state.isSaving = false
    },
    saveFormFail: state => {
      state.isSaving = false
    },
  }
})

const {
  loadCount,
  loadCountSuccess,
  loadCountFail,
  loadForm,
  loadFormSuccess,
  loadFormFail,
  saveForm,
  saveFormSuccess,
  saveFormFail,
} = slice.actions

export function loadAdvertisingLawFormCount() {
  return requestWrapper<AdvertisingLawFormCountResponse, number>(makeRequest('/saduka/advertisingLawForms/count', {
    types: [
      loadCount.type,
      loadCountSuccess.type,
      loadCountFail.type,
    ],
    options: {
      credentials: 'include',
    }
  }), normalizeAdvertisingLawFormCountResponse)
}

export function loadAdvertisingLawForm() {
  return requestWrapper<AdvertisingLawFormListResponse, AdvertisingLawForm | null>(makeRequest('/saduka/advertisingLawForms', {
    types: [
      loadForm.type,
      loadFormSuccess.type,
      loadFormFail.type,
    ],
    options: {
      credentials: 'include',
    }
  }), normalizeAdvertisingLawFormListResponse)
}

export function saveAdvertisingLawForm(data: AdvertisingLawForm) {
  return requestWrapper<AdvertisingLawFormResponse, AdvertisingLawForm>(makeRequest('/saduka/advertisingLawForms', {
    types: [
      saveForm.type,
      saveFormSuccess.type,
      saveFormFail.type,
    ],
    options: {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ advertisingLawForm: data })
    }
  }), normalizeAdvertisingLawFormResponse)
}

export default slice.reducer
